<template>
  <div>
    <NavBar sticky class="trade-search-view__navbar">
      <template #left>
        <BackButton @click="onBackClick" />
      </template>
      <template #center>Buscar Ativo</template>
    </NavBar>
    <Loading v-if="loading" />
    <ActionableHelperView v-else-if="error" :on-retry="getSearchRequest" />
    <template v-else>
      <div class="trade-search-view__wrapper">
        <AutoComplete search-value="" :recent-instruments="recentInstruments" />
      </div>
      <div class="trade-search-view__content">
        <FiltersList
          :filters="pageContentData.filters"
          @select-filter="handleSelectFilter"
        />
        <div class="trade-search-view__content--divider" />
        <SectionsList
          :filter="currentFilter"
          :highlights="pageContentData.marketHighLight"
          :section-types="pageContentData.sectors"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import {
  NavBar,
  ActionableHelperView,
  IFilterOption
} from '@warrenbrasil/nebraska-web';
import { AutoComplete } from './components/AutoComplete';
import { Loading } from './components/Loading';
import { SectionsList } from './components/SectionsList';
import { FiltersList } from '@/modules/trade/components/FiltersList';
import { BackButton } from '@/modules/common/components/buttons/BackButton';

import {
  PageContent,
  Instrument,
  getSearchPageContent
} from '@/modules/trade/services/search';
import { IDropdownItem, dropdownItemTypes } from './components/Dropdown';
import { trackFilterClick, trackSearchViewLoad } from './trackers';
import { useCatalogStore } from '@/modules/catalog/store';

const defaultFilter = {
  formatted: 'Ações',
  value: 'stock'
};

@Component({
  components: {
    NavBar,
    BackButton,
    AutoComplete,
    Loading,
    ActionableHelperView,
    FiltersList,
    SectionsList
  }
})
export default class SearchView extends Vue {
  private loading = true;
  private error = false;
  private pageContentData!: PageContent;
  private recentInstruments: IDropdownItem[] = [];
  private currentFilter = defaultFilter;

  private handleSelectFilter(filter: IFilterOption) {
    trackFilterClick(filter.label);
    this.currentFilter = {
      formatted: filter.label,
      value: filter.value
    };
    this.getSearchRequest();
  }

  private getSearchRequest() {
    const filter = this.currentFilter.value;
    this.loading = true;
    this.error = false;
    getSearchPageContent(filter)
      .then(searchData => {
        this.pageContentData = searchData;
        this.currentFilter = searchData.filters.available.find(
          filter => filter.value === searchData.filters.selected
        )!;
        this.recentInstruments = this.getFormattedRecentInstruments(
          this.pageContentData.recentSearches
        );
      })
      .catch(() => {
        this.error = true;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private getFormattedRecentInstruments(recentSearches: Instrument[]) {
    let dropdownItems: IDropdownItem[] = recentSearches.map(instrument => ({
      type: dropdownItemTypes.option,
      data: { ...instrument }
    }));

    if (dropdownItems.length > 0) {
      dropdownItems = [
        { type: dropdownItemTypes.section, data: { title: 'Recentes' } },
        ...dropdownItems
      ];
    }

    return dropdownItems;
  }

  private created() {
    this.getSearchRequest();
    trackSearchViewLoad();
  }

  private onBackClick() {
    if (useCatalogStore().fromUserFlow) {
      this.$router.back();
    } else {
      this.$router.push({ name: 'trade' });
    }
  }
}
</script>

<style lang="less" scoped>
.trade-search-view {
  &__navbar {
    margin-bottom: @size-spacing-x1100;
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    max-width: calc(800px + 2 * @size-spacing-x800);
    margin: 0 auto;
    padding: 0 @size-spacing-x400;
  }
  &__content {
    max-width: 1200px;
    margin: 0 auto;
    padding: @size-spacing-x800 @size-spacing-x400 @size-spacing-x800
      @size-spacing-x400;
    &--divider {
      border-bottom: 1px solid @divider-primary;
      margin-top: @size-spacing-x400;
      margin-bottom: @size-spacing-x800;
    }
  }
}

@media @notebookUp {
  .trade-search-view {
    &__wrapper {
      padding: 0 @size-spacing-x800;
    }
    &__content {
      padding: @size-spacing-x800;
    }
  }
}
</style>
